import { FC } from 'react';
import { Box, Card, formatAmount, formatDate, Tooltip, toPascalCase, withStyles } from 'vopay';
import styles from './VerificationCard.module.scss';
import { Button } from '@/components/UI';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { InteracWithExtra } from '../PaymentMethodDetails';
import useClientAccountPaymentDetails from '@/hooks/usePaymentMethodDetails';
import { useToast } from '@/providers/Toast.provider';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@/providers/Dialog.provider';
import UpgradePlanDialog from '@/components/Customer/Tabs/PaymentMethods/UpgradePlanDialog/UpgradePlanDialog';
import EnhancedVerificationDialog from '@/components/Customer/Tabs/PaymentMethods/EnhancedVerificationDialog/EnhancedVerificationDialog';

const cn = withStyles(styles);

type VerificationCardProps = {
  interac: InteracWithExtra;
  accountID: string;
  email: string;
  isInEditMode?: boolean;
  refreshInteracDetails: () => void;
};

const VerificationCard: FC<VerificationCardProps> = ({
  interac,
  accountID,
  email,
  isInEditMode,
  refreshInteracDetails,
}) => {
  const { InteracBasicVerificationEnabled, InteracEnhancedVerificationEnabled } = useLockedFeatures();
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.PaymentMethodsTab.Details' });
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const dialog = useDialog();

  const { verifyInteracAccount } = useClientAccountPaymentDetails({
    accountID,
    email,
  });

  const handleInteracSendVerification = async () => {
    const closeType = await alertDialog.open(
      'Send Verification Request?',
      'We will check if the name provided for this email address matches the registered name on Interac.',
      {
        confirmLabel: 'Confirm',
        bottom: 'This action does not notify the client',
        abortLabel: 'Cancel',
      },
    );
    if (closeType === 'confirm') {
      try {
        const res = await verifyInteracAccount({
          VerificationTier: 'basic',
          EmailAddress: interac.RecipientEmailAddress || '',
          ContactID: accountID,
        });
        if (res.Success) {
          toast.success('Interac Verification request sent');
          await refreshInteracDetails();
        }
      } catch (err) {
        if (err instanceof Error) alertDialog.error('Error', err.message);

        console.error(err);
      }
    }
  };

  const handleEnhancedInteracSendVerification = async () => {
    const closeType = await alertDialog.open(
      'Send Verification Request?',
      <>
        <Box flx="col" ai="fs" p="sm">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'left',
              gap: '24px',
              color: 'rgba(45, 45, 45, 0.75)',
            }}
          >
            <p>We will check if the name provided for this email address matches the registered name on Interac.</p>
            <div>
              <p>We will also retrieve the following details:</p>
              <ul style={{ listStylePosition: 'outside', paddingLeft: '20px' }}>
                <li>Registered Interac Name</li>
                <li>Alias</li>
                <li>Account Type</li>
                <li>Auto-Deposit Status</li>
                <li>Transaction Limit</li>
              </ul>
            </div>
          </div>
        </Box>
      </>,
      {
        confirmLabel: 'Confirm',
        bottom: 'This action does not notify the client',
        abortLabel: 'Cancel',
      },
    );
    if (closeType === 'confirm') {
      try {
        const res = await verifyInteracAccount({
          VerificationTier: 'enhanced',
          EmailAddress: interac.RecipientEmailAddress || '',
          ContactID: accountID,
        });

        if (res.Success) {
          toast.success('Interac Verification request sent');
          await refreshInteracDetails();
        }
      } catch (err) {
        if (err instanceof Error) alertDialog.error('Error', err.message);

        console.error(err);
      }
    }
  };

  function getAutoDepositStatus(value: string): string {
    if (value === '1') return 'Enabled';
    if (value === '0') return 'Disabled';
    if (value === 'N/A') return 'N/A';
    return '-';
  }

  function getTransactionLimit(value: string): string {
    if (value === 'N/A') return 'N/A';
    if (value) return `${interac.Currency} ${formatAmount(interac.TransactionLimit)}`;
    return '-';
  }

  const rows = [
    {
      key: 'Verification Status',
      val: (
        <>
          <p className={cn('verificationLabel', interac.verificationStatus)}>
            {interac.verificationStatus === 'verified' ? (
              t('Verification.verified', { defaultValue: 'Verified' })
            ) : interac.verificationStatus === 'unverified' ? (
              <>
                {t('Verification.notVerified', { defaultValue: 'Not Verified' })}
                {interac.verificationSentOn && (
                  <Tooltip
                    position="topLeft"
                    icon="info"
                    information="This address could not be verified because it is not registered with Interac. Please ensure the email is linked to an Interac account."
                  />
                )}
              </>
            ) : (
              t('Verification.failed', { defaultValue: 'Verification Failed' })
            )}
          </p>
        </>
      ),
    },
    { key: 'Registered Interac Name', val: interac.RegisteredName },
    { key: 'Alias', val: interac.Alias },
    { key: 'Account Type', val: toPascalCase(interac.CustomerType) },
    {
      key: 'Auto-Deposit',
      val: getAutoDepositStatus(interac.AutoDepositEnabled),
    },
    {
      key: 'Transaction Limit',
      val: getTransactionLimit(interac.TransactionLimit),
    },
  ].map((row, index) => ({ key: row.key, val: row.val || index === 0 ? row.val : '-' }));

  const interacVerificationLockedStyle = !InteracBasicVerificationEnabled
    ? 'fullyLocked'
    : !interac.verificationSentOn && !InteracEnhancedVerificationEnabled
      ? 'partiallyLocked'
      : undefined;

  async function handleUnlockVerificationRequestClick() {
    await dialog.open<boolean>((args) => <UpgradePlanDialog {...args} />);
  }

  async function handleUnlockEnhancedVerificationRequestClick() {
    await dialog.open<boolean>((args) => <EnhancedVerificationDialog {...args} />);
  }

  return (
    <div
      className={cn(
        'verificationCard',
        InteracEnhancedVerificationEnabled && 'enhanced',
        interacVerificationLockedStyle,
      )}
    >
      <Card style={{ padding: '16px' }} hoverEffect={false}>
        <span className={cn('cardContent')}>
          <div className={cn('header')}>
            <h5 className={cn('title')}>Verification Details</h5>
            {interac?.verificationSentOn && (
              <p className={cn('requestSentOn')}>
                Last request sent on {formatDate(interac.verificationSentOn, 'yyyy-MM-dd HH:mm:ss')}
              </p>
            )}
          </div>
          <span>
            {rows.map(({ key, val }) => (
              <div key={key} className={cn('row')}>
                <div>{key}</div>
                <div>{val}</div>
              </div>
            ))}
          </span>
          <div className={cn('footer')}>
            {!InteracBasicVerificationEnabled && (
              <Button
                iconRightProps={{ icon: 'lock', fillAmount: 1 }}
                size="small"
                colorScheme="primary"
                disabled={isInEditMode}
                onClick={handleUnlockVerificationRequestClick}
              >
                Unlock Verification Request
              </Button>
            )}

            {InteracBasicVerificationEnabled && (
              <div className={cn('buttonContainer')}>
                <Button size="small" onClick={handleInteracSendVerification}>
                  Basic Verification Request
                </Button>

                {!InteracEnhancedVerificationEnabled ? (
                  <Button
                    colorScheme="primary"
                    iconRightProps={{ icon: 'lock', fillAmount: 1 }}
                    size="small"
                    onClick={handleUnlockEnhancedVerificationRequestClick}
                  >
                    Unlock Enhanced Verification
                  </Button>
                ) : (
                  <Button
                    colorScheme="feature"
                    size="small"
                    iconRightProps={{ icon: 'auto_awesome', color: '#fbfbfb', fillAmount: 1 }}
                    onClick={handleEnhancedInteracSendVerification}
                  >
                    Enhanced Verification Request
                  </Button>
                )}
              </div>
            )}
          </div>
        </span>
      </Card>
    </div>
  );
};

export default VerificationCard;
